/**
 * Product Bubbles Functionality
 * Handles bubble interactions and product switching
 */

class ProductBubbles {
  constructor() {
    this.bubbleContainers = document.querySelectorAll('.product-bubbles');
    this.cache = window.BubbleCollectionCache ? BubbleCollectionCache.getInstance() : null;
    this.debug = false; // Set to true to enable console logging
    this.flickityResizeTimer = null; // Timer for debouncing Flickity resize calls
    this.init();
  }

  init() {
    if (!this.bubbleContainers.length) return;

    this.bubbleContainers.forEach(container => {
      this.initContainer(container);
    });
  }

  initContainer(container) {
    // Skip if already initialized (unless forcing reinitialization)
    if (container.dataset.bubblesInitialized === 'true' && !container.dataset.reinitialize) {
      return;
    }

    // Check if container wants to use cache mode
    if (container.dataset.useCache === 'true' && this.cache) {
      this.initContainerWithCache(container);
      return;
    }

    const bubbles = container.querySelectorAll('.product-bubble');
    let context = container.dataset.context;
    
    // check if the container has a data-section attribute
    
    // Check if we're in a quick view modal
    const isQuickView = container.closest('#Product-Drawer') !== null;
    if (isQuickView) {
      context = 'quickview';
      // Update the context data attribute for clarity
      container.dataset.context = 'quickview';
      // this.debug && console.log('Detected quick view context for bubble container');
    }
    
    bubbles.forEach(bubble => {
      // Remove any existing click handlers to prevent duplicates
      const newBubble = bubble.cloneNode(true);
      bubble.parentNode.replaceChild(newBubble, bubble);
      
      newBubble.addEventListener('click', (e) => {
        // console.log(`Bubble clicked in context: ${context}`);
        
        // Don't switch if already active or sold out
        if (newBubble.classList.contains('product-bubble--active') || 
            newBubble.classList.contains('product-bubble--sold-out')) {
          e.preventDefault(); // Still prevent click on active/sold out
          return;
        }
        
        // Handle based on context
        if (context === 'collection') {
          // Use AJAX for collection pages
          e.preventDefault();
          this.handleCollectionSwitch(newBubble, container);
        } else if (context === 'quickview') {
          // Use AJAX for quick view modal
          e.preventDefault();
          // this.debug && console.log('Handling quick view switch...');
          this.handleQuickViewSwitch(newBubble, container);
        } else {
          // Use regular links for product pages (keep current behavior)
          const productUrl = newBubble.dataset.productUrl;
          if (productUrl) {
            window.location.href = productUrl;
          }
        }
      });
      
      // Add hover effect
      newBubble.addEventListener('mouseenter', () => {
        this.showTooltip(newBubble);
      });
      
      newBubble.addEventListener('mouseleave', () => {
        this.hideTooltip(newBubble);
      });
    });
    
    // Mark container as initialized
    container.dataset.bubblesInitialized = 'true';
    container.dataset.reinitialize = 'false';
  }

  handleProductPageSwitch(bubble, container) {
    const productData = {
      id: bubble.dataset.productId,
      handle: bubble.dataset.productHandle,
      title: bubble.dataset.productTitle,
      price: bubble.dataset.productPrice,
      image: bubble.dataset.productImage,
      url: bubble.dataset.productUrl,
      variantId: bubble.dataset.variantId
    };
    
    // this.debug && console.log('Bubble clicked - Product data:', productData);
    
    // Show loading state
    this.setLoading(container, true);
    
    // Update URL without reload
    const newUrl = productData.url;
    window.history.pushState({ productId: productData.id }, '', newUrl);
    
    // Update page content
    this.updateProductPage(productData).then(() => {
      // Update active bubble
      this.setActiveBubble(bubble, container);
      this.setLoading(container, false);
      
      // Trigger custom event
      this.triggerProductChange(productData);
    });
  }

  handleCollectionSwitch(bubble, container) {
    // Find the product card - try multiple selectors for compatibility
    let productCard = container.closest('.product-card');
    if (!productCard) {
      productCard = container.closest('product-card'); // Custom element version
    }
    if (!productCard) {
      productCard = container.closest('li')?.querySelector('.product-card, product-card');
    }
    
    if (!productCard) {
      // console.error('Product card not found for bubble container');
      // Fallback to navigation
      window.location.href = bubble.dataset.productUrl;
      return;
    }
    
    const productData = {
      id: bubble.dataset.productId,
      handle: bubble.dataset.productHandle,
      title: bubble.dataset.productTitle,
      price: bubble.dataset.productPrice,
      image: bubble.dataset.productImage,
      url: bubble.dataset.productUrl,
      variantId: bubble.dataset.variantId
    };
    
    // console.log('Collection bubble clicked - Product data:', productData);
    // console.log('Product card found:', productCard);
    
    // Show loading state
    this.setLoading(container, true);
    
    // Update product card
    this.updateProductCard(productCard, productData).then(() => {
      // Update active bubble
      this.setActiveBubble(bubble, container);
      this.setLoading(container, false);

      // Trigger custom event for other scripts
      this.triggerProductChange(productData);

      // Trigger Flickity resize after card update (bubbles may have changed)
      this.triggerFlickityResize(container);
    }).catch(error => {
      // console.error('Error in handleCollectionSwitch:', error);
      this.setLoading(container, false);
      // Fallback to navigation on error
      window.location.href = bubble.dataset.productUrl;
    });
  }

  handleQuickViewSwitch(bubble, container) {
    const quickViewModal = container.closest('#Product-Drawer');
    if (!quickViewModal) {
      // console.error('Quick view modal not found');
      // Fallback to navigation
      window.location.href = bubble.dataset.productUrl;
      return;
    }
    
    const productData = {
      id: bubble.dataset.productId,
      handle: bubble.dataset.productHandle,
      title: bubble.dataset.productTitle,
      price: bubble.dataset.productPrice,
      image: bubble.dataset.productImage,
      url: bubble.dataset.productUrl,
      variantId: bubble.dataset.variantId
    };
    
    // console.log('Quick view bubble clicked - Product data:', productData);
    // console.log('Quick view modal found:', quickViewModal);
    
    // Show loading state
    this.setLoading(container, true);
    
    // Update quick view modal content
    this.updateQuickView(quickViewModal, productData).then(() => {
      // Update active bubble
      this.setActiveBubble(bubble, container);
      this.setLoading(container, false);
      
      // Trigger custom event for other scripts
      this.triggerProductChange(productData);
      
      // Dispatch event that quick view content has changed
      document.dispatchEvent(new CustomEvent('quickview:productChanged', {
        detail: productData
      }));
    }).catch(error => {
      // console.error('Error in handleQuickViewSwitch:', error);
      this.setLoading(container, false);
      // Fallback to navigation on error
      window.location.href = bubble.dataset.productUrl;
    });
  }

  async updateProductPage(productData) {
    // this.debug && console.log('=== BUBBLE UPDATE START ===');
    // console.log('Product data received:', productData);
    
    // Debug: List all important elements
    // console.log('Debug: Searching for elements...');
    // console.log('Images found:', document.querySelectorAll('.product-single__media-image img, .product-images__slide img, .product__image img').length);
    // console.log('Titles found:', document.querySelectorAll('.product-title, h1').length);
    // console.log('Prices found:', document.querySelectorAll('.price .amount, .price ins .amount, .product-price-container .amount').length);
    
    // First, fetch full product data
    try {
      // console.log('Fetching full product data from:', productData.url + '.js');
      const response = await fetch(productData.url + '.js');
      const fullProductData = await response.json();
      // console.log('Full product data fetched:', fullProductData);
      
      // Update main product image
      const mainImageSelectors = [
        '.product-single__media-image img',
        '.product-images__slide.is-active img',
        '.product__image.is-active img',
        '.product-single__photo img'
      ];
      
      let imageUpdated = false;
      for (const selector of mainImageSelectors) {
        const img = document.querySelector(selector);
        if (img) {
          // console.log(`Found main image with selector: ${selector}`);
          img.src = productData.image;
          img.srcset = productData.image;
          img.alt = fullProductData.title;
          imageUpdated = true;
          break;
        }
      }
      
      if (!imageUpdated) {
        // console.warn('Main image not found, trying first visible image...');
        const firstImage = document.querySelector('.product-images img');
        if (firstImage) {
          firstImage.src = productData.image;
          firstImage.srcset = productData.image;
          firstImage.alt = fullProductData.title;
        }
      }
      
      // Update product title
      const titleElement = document.querySelector('.product-title, .product-title-container h1');
      if (titleElement) {
        // console.log('Found title element, updating to:', fullProductData.title);
        titleElement.textContent = fullProductData.title;
      } else {
        // console.warn('Title element not found');
      }
      
      // Update price - looking for the actual price amount
      const priceSelectors = [
        '.price .amount',
        '.price ins .amount',
        '.product-price-container .amount',
        '.product-price-container .price .amount',
        '[id^="price-"] .amount'
      ];
      
      let priceUpdated = false;
      for (const selector of priceSelectors) {
        const priceEl = document.querySelector(selector);
        if (priceEl) {
          // console.log(`Found price with selector: ${selector}`);
          // Format price from fullProductData
          const formattedPrice = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(fullProductData.price / 100);
          priceEl.textContent = formattedPrice;
          priceUpdated = true;
          break;
        }
      }
      
      if (!priceUpdated) {
        // console.warn('Price element not found with any selector');
      }
      
      // Update product description
      const descSelectors = [
        '.product-short-description.rte', // Your specific selector
        '.product-short-description',
        '.product__description',
        '.product-description',
        '[data-product-description]',
        '.collapsible_tab__content .rte' // Common in accordion/tab layouts
      ];
      
      let descriptionUpdated = false;
      for (const selector of descSelectors) {
        const descEl = document.querySelector(selector);
        if (descEl && fullProductData.description) {
          // console.log(`Found description with selector: ${selector}`);
          descEl.innerHTML = fullProductData.description;
          descriptionUpdated = true;
          break;
        }
      }
      
      if (!descriptionUpdated && fullProductData.description) {
        // console.warn('Description element not found with any selector');
        // Try to find any element with 'description' in its class
        const anyDescEl = document.querySelector('[class*="description"]');
        if (anyDescEl) {
          // console.log('Found description element with partial class match:', anyDescEl.className);
          anyDescEl.innerHTML = fullProductData.description;
        }
      }
      
      // Update add to cart form
      const addToCartForm = document.querySelector('form[action="/cart/add"]');
      if (addToCartForm) {
        const variantInput = addToCartForm.querySelector('input[name="id"]');
        if (variantInput && fullProductData.variants[0]) {
          // console.log('Updating variant ID to:', fullProductData.variants[0].id);
          variantInput.value = fullProductData.variants[0].id;
        }
        
        // Update product ID if present
        const productInput = addToCartForm.querySelector('input[name="product-id"]');
        if (productInput) {
          productInput.value = fullProductData.id;
        }
      }
      
      // Update inventory/availability
      if (fullProductData.available === false) {
        const addButton = document.querySelector('.product-form__submit, [name="add"]');
        if (addButton) {
          addButton.disabled = true;
          addButton.textContent = 'Sold Out';
        }
      } else {
        const addButton = document.querySelector('.product-form__submit, [name="add"]');
        if (addButton) {
          addButton.disabled = false;
          addButton.textContent = addButton.dataset.addToCartText || 'Add to Cart';
        }
      }
      
      // Update inventory notice
      const inventoryNotice = document.querySelector('.product-inventory-notice--text');
      if (inventoryNotice && fullProductData.variants) {
        // console.log('Checking inventory data in product:', fullProductData);
        
        // Get the first available variant's inventory
        const availableVariant = fullProductData.variants.find(v => v.available) || fullProductData.variants[0];
        // console.log('Available variant for inventory:', availableVariant);
        
        // Try different ways to get inventory
        let inventoryQty = null;
        
        // Method 1: Check if inventory_quantity is directly available
        if (availableVariant && availableVariant.inventory_quantity !== undefined) {
          inventoryQty = availableVariant.inventory_quantity;
          // console.log('Found inventory_quantity directly:', inventoryQty);
        }
        
        // Method 2: If inventory not in basic data, we might need to fetch it separately
        // For now, let's try to get it from the page itself by fetching the HTML
        if (inventoryQty === null && productData.url) {
          // console.log('Inventory not in JSON, fetching from product page...');
          
          // Fetch the product page HTML to get inventory info
          fetch(productData.url)
            .then(response => response.text())
            .then(html => {
              // Parse the HTML to find inventory info
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const inventoryElement = doc.querySelector('.product-inventory-notice--text');
              
              if (inventoryElement) {
                const inventoryMatch = inventoryElement.textContent.match(/(\d+)\s+in stock/);
                if (inventoryMatch) {
                  inventoryQty = parseInt(inventoryMatch[1]);
                  // console.log('Found inventory from HTML:', inventoryQty);
                  
                  // Update the display
                  const stockText = `${inventoryQty} in stock`;
                  inventoryNotice.innerHTML = `<div class="product-inventory-notice--pulse"></div>${stockText}`;
                  
                  // Update inventory bar scale
                  const inventoryBar = document.querySelector('.product-inventory-notice--bar');
                  if (inventoryBar) {
                    const maxStock = 20; // Assuming max display is 20
                    const scale = Math.min(inventoryQty / maxStock, 1);
                    inventoryBar.style.setProperty('--inventory-scale', scale);
                  }
                  
                  // Update inventory notice class based on stock level
                  const noticeWrapper = document.querySelector('.product-inventory-notice');
                  if (noticeWrapper) {
                    noticeWrapper.classList.remove('product-inventory-notice--low', 'product-inventory-notice--high');
                    if (inventoryQty <= 5) {
                      noticeWrapper.classList.add('product-inventory-notice--low');
                    } else {
                      noticeWrapper.classList.add('product-inventory-notice--high');
                    }
                  }
                }
              }
            })
            .catch(err => {
              // console.error('Error fetching inventory from page:', err);
            });
        }
        
        // If we have inventory from the JSON, update it immediately
        if (inventoryQty !== null) {
          const stockText = `${inventoryQty} in stock`;
          // console.log('Updating inventory notice to:', stockText);
          inventoryNotice.innerHTML = `<div class="product-inventory-notice--pulse"></div>${stockText}`;
          
          // Update inventory bar scale
          const inventoryBar = document.querySelector('.product-inventory-notice--bar');
          if (inventoryBar) {
            const maxStock = 20; // Assuming max display is 20
            const scale = Math.min(inventoryQty / maxStock, 1);
            inventoryBar.style.setProperty('--inventory-scale', scale);
          }
          
          // Update inventory notice class based on stock level
          const noticeWrapper = document.querySelector('.product-inventory-notice');
          if (noticeWrapper) {
            noticeWrapper.classList.remove('product-inventory-notice--low', 'product-inventory-notice--high');
            if (inventoryQty <= 5) {
              noticeWrapper.classList.add('product-inventory-notice--low');
            } else {
              noticeWrapper.classList.add('product-inventory-notice--high');
            }
          }
        } else if (!productData.url) {
          // If we can't get inventory, at least update based on availability
          // console.log('No inventory data available, updating based on availability');
          if (!availableVariant || !availableVariant.available) {
            inventoryNotice.innerHTML = `<div class="product-inventory-notice--pulse"></div>Out of stock`;
            const noticeWrapper = document.querySelector('.product-inventory-notice');
            if (noticeWrapper) {
              noticeWrapper.classList.remove('product-inventory-notice--high');
              noticeWrapper.classList.add('product-inventory-notice--low');
            }
          }
        }
      }
      
      // Update variant selectors (sizes)
      this.updateVariantSelectors(fullProductData);
      
      // Update page title
      document.title = fullProductData.title + ' – ' + (window.theme?.shopName || 'Smith & Quinn');
      
      // Update any image galleries
      this.updateImageGallery(fullProductData);
      
      // Update other product details
      this.updateProductDetails(fullProductData);
      
      // this.debug && console.log('=== BUBBLE UPDATE COMPLETE ===');

    } catch (error) {
      // console.error('Error updating product:', error);
      // Fallback: just reload the page
      // console.log('Falling back to page reload...');
      window.location.href = productData.url;
    }
  }

  async updateQuickView(modal, productData) {
    // this.debug && console.log('=== QUICK VIEW UPDATE START ===');
    // console.log('Updating quick view with:', productData);

    try {
      // Fetch full product data
      const response = await fetch(productData.url + '.js');
      const fullProductData = await response.json();
      // console.log('Full product data fetched:', fullProductData);

      // 1. Update product images in the gallery
      const imageSlides = modal.querySelectorAll('.product-quick-images__slide');
      // console.log(`Found ${imageSlides.length} image slides in quick view`);
      
      if (fullProductData.images && fullProductData.images.length > 0) {
        imageSlides.forEach((slide, index) => {
          const img = slide.querySelector('img');
          if (img && index < fullProductData.images.length) {
            // Update image with appropriate sizes for quick view
            const imageUrl = fullProductData.images[index];
            const baseUrl = imageUrl.split('?')[0];
            const imageSrcset = `${baseUrl}?v=${Date.now()}&width=375 375w,${baseUrl}?v=${Date.now()}&width=640 640w,${baseUrl}?v=${Date.now()}&width=960 960w`;
            
            img.src = `${baseUrl}?v=${Date.now()}&width=20`;
            img.dataset.src = `${baseUrl}?v=${Date.now()}&width=20`;
            img.dataset.srcset = imageSrcset;
            img.srcset = imageSrcset;
            img.alt = fullProductData.title;
            
            // Update lazy loading classes
            img.classList.add('lazyautosizes', 'lazyloaded');
            
            // Show the slide
            slide.style.display = '';
            // console.log(`Updated quick view image ${index + 1}`);
          } else if (img) {
            // Hide extra slides if new product has fewer images
            slide.style.display = 'none';
          }
        });
      }
      
      // 2. Update product title
      const titleElement = modal.querySelector('.product-title');
      if (titleElement) {
        titleElement.textContent = fullProductData.title;
        // console.log('Updated quick view title');
      }
      
      // 3. Update price
      const priceElement = modal.querySelector('.price .amount');
      if (priceElement && fullProductData.price) {
        const formattedPrice = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(fullProductData.price / 100);
        priceElement.textContent = formattedPrice;
        // console.log('Updated quick view price to:', formattedPrice);
      }
      
      // 4. Update variant selectors (sizes)
      const variantSelects = modal.querySelector('variant-selects');
      if (variantSelects && fullProductData.handle) {
        // Update URL for variant selects
        variantSelects.dataset.url = `/products/${fullProductData.handle}`;
        
        // Update the variant JSON data
        const variantScript = variantSelects.querySelector('script[type="application/json"]');
        if (variantScript && fullProductData.variants) {
          variantScript.textContent = JSON.stringify(fullProductData.variants);
          // console.log('Updated variant JSON data');
        }
        
        // Update size options
        const sizeFieldset = modal.querySelector('fieldset[data-handle="size"]');
        if (sizeFieldset && fullProductData.variants) {
          this.updateQuickViewSizes(sizeFieldset, fullProductData.variants, modal);
        }
      }
      
      // 5. Update form IDs and product IDs
      const productForm = modal.querySelector('form[action="/cart/add"]');
      if (productForm && fullProductData.variants && fullProductData.variants.length > 0) {
        // Get first available variant
        const selectedVariant = fullProductData.variants.find(v => v.available) || fullProductData.variants[0];
        
        // Update hidden variant ID input
        const variantInput = productForm.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = selectedVariant.id;
          // console.log('Updated variant ID to:', selectedVariant.id);
        }
        
        // Update product ID input
        const productIdInput = productForm.querySelector('input[name="product-id"]');
        if (productIdInput) {
          productIdInput.value = fullProductData.id;
          // console.log('Updated product ID to:', fullProductData.id);
        }
        
        // Update quantity input attributes
        const quantityInput = productForm.querySelector('input[name="quantity"]');
        if (quantityInput) {
          quantityInput.setAttribute('aria-label', `Quantity for ${fullProductData.title}`);
        }
        
        // Update button states based on availability
        const addButton = productForm.querySelector('button[type="submit"]');
        if (addButton) {
          if (selectedVariant.available) {
            addButton.disabled = false;
            addButton.querySelector('.single-add-to-cart-button--text').textContent = 'Add to cart';
          } else {
            addButton.disabled = true;
            addButton.querySelector('.single-add-to-cart-button--text').textContent = 'Sold out';
          }
        }
      }
      
      // 6. Update payment button data
      const paymentButton = modal.querySelector('shopify-payment-terms');
      if (paymentButton && fullProductData.variants) {
        paymentButton.setAttribute('variant-id', fullProductData.variants[0].id);
        // Update the shopify-meta attribute with new variant data
        const metaData = {
          type: 'product',
          currency_code: 'USD',
          country_code: 'US',
          variants: fullProductData.variants.map(v => ({
            id: v.id,
            price_per_term: `$${(v.price / 400).toFixed(2)}`,
            full_price: `$${(v.price / 100).toFixed(2)}`,
            eligible: true,
            available: v.available,
            number_of_payment_terms: 4
          }))
        };
        paymentButton.setAttribute('shopify-meta', JSON.stringify(metaData));
      }
      
      // this.debug && console.log('=== QUICK VIEW UPDATE COMPLETE ===');

    } catch (error) {
      // console.error('Error updating quick view:', error);
      throw error;
    }
  }
  
  updateQuickViewSizes(sizeFieldset, variants, modal) {
    // console.log('Updating quick view size options...');
    
    // Get all size inputs and labels
    const sizeInputs = sizeFieldset.querySelectorAll('input[type="radio"]');
    const sizeLabels = sizeFieldset.querySelectorAll('label');
    
    // Create a map of available sizes from new product
    const availableSizes = new Map();
    variants.forEach(variant => {
      const size = variant.option1 || variant.options?.[0];
      if (size) {
        availableSizes.set(size, variant);
      }
    });
    
    // Update existing size options
    sizeInputs.forEach((input, index) => {
      const label = sizeLabels[index];
      const size = input.value;
      const variant = availableSizes.get(size);
      
      if (variant) {
        // Show and update this size option
        input.style.display = '';
        input.value = size;
        if (label) {
          label.style.display = '';
        }
        
        // Update availability
        if (variant.available) {
          input.disabled = false;
          input.classList.remove('is-disabled');
        } else {
          input.disabled = true;
          input.classList.add('is-disabled');
        }
        
        // Update the input name and form association
        input.name = 'Size';
        
        // Select first available size
        if (variant.available && !sizeFieldset.querySelector('input:checked')) {
          input.checked = true;
          // Update size display
          const sizeDisplay = sizeFieldset.querySelector('.form__label__value');
          if (sizeDisplay) {
            sizeDisplay.textContent = size;
          }
        }
      } else {
        // Hide sizes that don't exist for this product
        input.style.display = 'none';
        if (label) {
          label.style.display = 'none';
        }
      }
    });
    
    // console.log('Quick view sizes updated');
  }

  async updateProductCard(card, productData) {
    // this.debug && console.log('=== COLLECTION CARD UPDATE START ===');
    // console.log('Updating product card with:', productData);

    try {
      // Fetch full product data for complete information
      const response = await fetch(productData.url + '.js');
      const fullProductData = await response.json();
      // console.log('Full product data fetched:', fullProductData);

      // 1. Update ALL product images (primary and secondary for hover effect)
      const allImages = card.querySelectorAll('.product-featured-image img');
      // console.log(`Found ${allImages.length} images in card`);
      
      if (fullProductData.images && fullProductData.images.length > 0) {
        allImages.forEach((img, index) => {
          if (index < fullProductData.images.length) {
            // Update image with appropriate size
            const imageUrl = fullProductData.images[index];
            // Generate responsive srcset for better performance
            const baseUrl = imageUrl.split('?')[0];
            const imageSrcset = `${baseUrl}?v=${Date.now()}&width=375 375w,${baseUrl}?v=${Date.now()}&width=770 770w`;
            
            img.src = `${baseUrl}?v=${Date.now()}&width=20`; // Small placeholder
            img.dataset.src = `${baseUrl}?v=${Date.now()}&width=20`;
            img.dataset.srcset = imageSrcset;
            img.srcset = imageSrcset;
            img.alt = fullProductData.title;
            
            // Handle lazy loading classes
            img.classList.add('lazyautosizes', 'ls-is-cached', 'lazyloaded');
            
            // console.log(`Updated image ${index + 1}: ${baseUrl}`);
          } else {
            // Hide extra images if new product has fewer images
            img.style.display = 'none';
          }
        });
        
        // Show images that were hidden if new product has them
        for (let i = 0; i < Math.min(allImages.length, fullProductData.images.length); i++) {
          allImages[i].style.display = '';
        }
      }
      
      // 2. Update the main product link
      const mainLink = card.querySelector('.product-featured-image-link');
      if (mainLink) {
        mainLink.href = `/collections/${window.location.pathname.split('/')[2]}/products/${productData.handle}`;
        mainLink.title = fullProductData.title;
        // console.log('Updated main product link');
      }
      
      // 3. Update product title
      const cardTitle = card.querySelector('.product-card-title');
      if (cardTitle) {
        cardTitle.textContent = fullProductData.title;
        cardTitle.href = `/collections/${window.location.pathname.split('/')[2]}/products/${productData.handle}`;
        cardTitle.title = fullProductData.title;
        // console.log('Updated product title');
      }
      
      // 4. Update price
      const priceElement = card.querySelector('.price .amount');
      if (priceElement && fullProductData.price) {
        // Format price properly
        const formattedPrice = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(fullProductData.price / 100);
        priceElement.textContent = formattedPrice;
        // console.log('Updated price to:', formattedPrice);
      }
      
      // 5. Update Quick View button
      const quickViewBtn = card.querySelector('quick-view, .product-card-quickview');
      if (quickViewBtn) {
        quickViewBtn.dataset.productHandle = productData.handle;
        // Update the data-product-handle attribute
        quickViewBtn.setAttribute('data-product-handle', productData.handle);
        // console.log('Updated quick view button handle');
      }
      
      // 6. Update secondary image navigation dots (if present)
      const imageNavDots = card.querySelectorAll('.product-secondary-images-nav li');
      if (imageNavDots.length > 0 && fullProductData.images) {
        imageNavDots.forEach((dot, index) => {
          if (index < fullProductData.images.length - 1) { // -1 because primary image is separate
            dot.style.display = '';
          } else {
            dot.style.display = 'none';
          }
        });
      }
      
      // this.debug && console.log('=== COLLECTION CARD UPDATE COMPLETE ===');

    } catch (error) {
      // console.error('Error updating product card:', error);
      // Fallback: navigate to product page on error
      // console.log('Falling back to navigation...');
      window.location.href = productData.url;
    }
  }

  updateProductDetails(productData) {
    // console.log('Updating additional product details...');

    // Update variant options if they exist
    const variantSelectors = document.querySelectorAll('.product-form__input');
    if (variantSelectors.length && productData.options) {
      // console.log('Updating variant options...');
      // This would need more complex logic based on theme structure
    }
    
    // Update product badges/tags
    const productTags = document.querySelector('.product__tags, .product-tags');
    if (productTags && productData.tags) {
      // console.log('Updating product tags...');
      const tagHtml = productData.tags.map(tag => 
        `<span class="product-tag">${tag}</span>`
      ).join(' ');
      productTags.innerHTML = tagHtml;
    }
    
    // Update breadcrumbs if needed
    const breadcrumb = document.querySelector('.breadcrumb__item--current, .breadcrumbs li:last-child');
    if (breadcrumb) {
      // console.log('Updating breadcrumb...');
      breadcrumb.textContent = productData.title;
    }
    
    // Update SKU if displayed
    const skuElement = document.querySelector('.product__sku, [data-sku]');
    if (skuElement && productData.variants?.[0]?.sku) {
      skuElement.textContent = productData.variants[0].sku;
    }
    
    // Update vendor if displayed
    const vendorElement = document.querySelector('.product__vendor, [data-vendor]');
    if (vendorElement && productData.vendor) {
      vendorElement.textContent = productData.vendor;
    }
  }
  
  updateVariantSelectors(productData) {
    // console.log('Updating variant selectors...');
    
    // Update the variant-selects element
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects && productData.handle) {
      // Update data attributes
      variantSelects.dataset.url = `/products/${productData.handle}`;
      
      // Update the embedded JSON script with new variant data
      const variantScript = variantSelects.querySelector('script[type="application/json"]');
      if (variantScript && productData.variants) {
        // console.log('Updating variant JSON data');
        variantScript.textContent = JSON.stringify(productData.variants);
      }
    }
    
    // Update size options
    const sizeFieldset = document.querySelector('fieldset[data-handle="size"]');
    if (sizeFieldset && productData.variants) {
      // console.log('Updating size options...');
      
      // Get all size inputs
      const sizeInputs = sizeFieldset.querySelectorAll('input[type="radio"]');
      const sizeLabels = sizeFieldset.querySelectorAll('label');
      
      // First, hide all existing options
      sizeInputs.forEach(input => {
        input.style.display = 'none';
        input.disabled = true;
        input.checked = false;
      });
      sizeLabels.forEach(label => {
        label.style.display = 'none';
      });
      
      // Then show and update only the ones that exist for this product
      productData.variants.forEach((variant, index) => {
        const sizeValue = variant.option1 || variant.options?.[0];
        if (sizeValue) {
          // Find the input for this size
          const input = Array.from(sizeInputs).find(inp => inp.value === sizeValue);
          const label = input ? sizeFieldset.querySelector(`label[for="${input.id}"]`) : null;
          
          if (input && label) {
            // Show and update this option
            input.style.display = '';
            label.style.display = '';
            
            // Set availability
            if (variant.available) {
              input.disabled = false;
              input.classList.remove('is-disabled');
            } else {
              input.disabled = true;
              input.classList.add('is-disabled');
            }
            
            // Check the first available variant
            if (index === 0 || (variant.available && !sizeFieldset.querySelector('input:checked'))) {
              input.checked = true;
              // Update the size display
              const sizeDisplay = sizeFieldset.querySelector('.form__label__value');
              if (sizeDisplay) {
                sizeDisplay.textContent = sizeValue;
              }
            }
          }
        }
      });
      
      // If no sizes match existing inputs, we might need to recreate them
      const visibleInputs = sizeFieldset.querySelectorAll('input[type="radio"]:not([style*="display: none"])');
      if (visibleInputs.length === 0 && productData.variants.length > 0) {
        // console.log('Need to recreate size options for different sizes');
        // This would require more complex DOM manipulation
        // For now, log that we need to handle this case
      }
    }
    
    // Update selected variant value in the form
    const variantIdInput = document.querySelector('input[name="id"][type="hidden"], select[name="id"]');
    if (variantIdInput && productData.variants?.[0]) {
      const selectedVariant = productData.variants.find(v => v.available) || productData.variants[0];
      variantIdInput.value = selectedVariant.id;
      // console.log('Updated variant ID input to:', selectedVariant.id);
    }
  }
  
  updateImageGallery(productData) {
    // console.log('Updating image gallery...');
    // console.log(`New product has ${productData.images ? productData.images.length : 0} images`);

    // Find the gallery container
    const galleryContainer = document.querySelector('.product-images, .product-single__media, .product__media-list');
    if (!galleryContainer) {
      // console.warn('Gallery container not found');
      return;
    }
    
    // Get all existing slides/images
    const existingSlides = galleryContainer.querySelectorAll('.product-images__slide, .product__media-item, .product-single__media-item');
    // console.log(`Currently ${existingSlides.length} slides in gallery`);
    
    if (productData.images && productData.images.length > 0) {
      // First, handle existing slides
      existingSlides.forEach((slide, index) => {
        if (index < productData.images.length) {
          // Update this slide with new image
          const img = slide.querySelector('img');
          if (img) {
            // console.log(`Updating slide ${index + 1} with new image`);
            img.src = productData.images[index];
            img.srcset = productData.images[index];
            img.alt = productData.title;
            // Make sure slide is visible
            slide.style.display = '';
            slide.classList.remove('hidden');
          }
        } else {
          // Hide extra slides that don't have corresponding images
          // console.log(`Hiding extra slide ${index + 1}`);
          slide.style.display = 'none';
          slide.classList.add('hidden');
        }
      });
      
      // If new product has MORE images than current slides, we need to add them
      if (productData.images.length > existingSlides.length) {
        // console.log(`Need to add ${productData.images.length - existingSlides.length} more image slides`);
        
        // Find a template slide to clone
        const templateSlide = existingSlides[0];
        if (templateSlide) {
          for (let i = existingSlides.length; i < productData.images.length; i++) {
            // console.log(`Creating new slide for image ${i + 1}`);
            const newSlide = templateSlide.cloneNode(true);
            const img = newSlide.querySelector('img');
            if (img) {
              img.src = productData.images[i];
              img.srcset = productData.images[i];
              img.alt = productData.title;
            }
            // Remove any active classes
            newSlide.classList.remove('is-active', 'active');
            newSlide.style.display = '';
            newSlide.classList.remove('hidden');
            galleryContainer.appendChild(newSlide);
          }
        }
      }
      
      // Update thumbnails separately
      const thumbnailContainer = document.querySelector('.product__thumbs, .product-single__thumbnails');
      if (thumbnailContainer) {
        const thumbnails = thumbnailContainer.querySelectorAll('img, button img');
        // console.log(`Updating ${thumbnails.length} thumbnails`);
        
        thumbnails.forEach((thumb, index) => {
          const thumbParent = thumb.closest('button, a, li');
          if (index < productData.images.length) {
            // Update thumbnail
            const thumbUrl = productData.images[index].replace(/\.(jpg|jpeg|png|webp)/, '_100x.$1');
            thumb.src = thumbUrl;
            thumb.srcset = thumbUrl;
            thumb.alt = productData.title;
            // Show thumbnail
            if (thumbParent) {
              thumbParent.style.display = '';
              thumbParent.classList.remove('hidden');
            }
          } else {
            // Hide extra thumbnails
            if (thumbParent) {
              thumbParent.style.display = 'none';
              thumbParent.classList.add('hidden');
            }
          }
        });
        
        // Add more thumbnails if needed
        if (productData.images.length > thumbnails.length && thumbnails.length > 0) {
          const templateThumb = thumbnails[0].closest('button, a, li');
          if (templateThumb && templateThumb.parentElement) {
            for (let i = thumbnails.length; i < productData.images.length; i++) {
              const newThumb = templateThumb.cloneNode(true);
              const img = newThumb.querySelector('img');
              if (img) {
                const thumbUrl = productData.images[i].replace(/\.(jpg|jpeg|png|webp)/, '_100x.$1');
                img.src = thumbUrl;
                img.srcset = thumbUrl;
                img.alt = productData.title;
              }
              newThumb.style.display = '';
              newThumb.classList.remove('hidden', 'is-active', 'active');
              templateThumb.parentElement.appendChild(newThumb);
            }
          }
        }
      }
      
      // Ensure first image is active
      if (existingSlides.length > 0) {
        existingSlides.forEach((slide, index) => {
          if (index === 0 && index < productData.images.length) {
            slide.classList.add('is-active', 'active');
          } else {
            slide.classList.remove('is-active', 'active');
          }
        });
      }
    }

    // console.log('Gallery update complete');
  }

  async initContainerWithCache(container) {
    // this.debug && console.log('[Bubbles] Initializing with cache mode');

    const collectionHandle = container.dataset.collectionHandle;
    const styleTag = container.dataset.styleTag;
    const currentProductId = container.dataset.currentProductId;
    const section = container.dataset.section || 'main';

    if (!collectionHandle || !styleTag) {
      // console.error('[Bubbles] Missing required data attributes for cache mode');
      return;
    }

    try {
      container.classList.add('product-bubbles--loading');

      // Define sort function for style_order
      const sortFn = (a, b) => {
        const orderA = parseInt(a.metafields?.style_order || 999999);
        const orderB = parseInt(b.metafields?.style_order || 999999);
        return orderA - orderB;
      };

      // Use bubble group cache (returns fully processed, sorted bubbles with metafields)
      const products = await this.cache.getBubbleGroup(
        collectionHandle,
        styleTag,
        section,
        sortFn
      );

      // this.debug && console.log(`[Bubbles] Loaded ${products.length} sorted bubbles for "${styleTag}"`);

      // Render bubbles (products already have metafields and are sorted)
      this.renderBubblesFromData(container, products, currentProductId, section);

      // Trigger Flickity resize for collection sliders
      // Has built-in retry mechanism if Flickity not ready yet
      this.triggerFlickityResize(container);

      // Initialize interactions
      const bubbles = container.querySelectorAll('.product-bubble');
      let context = container.dataset.context;

      // Check if we're in a quick view modal
      const isQuickView = container.closest('#Product-Drawer') !== null;
      if (isQuickView) {
        context = 'quickview';
        container.dataset.context = 'quickview';
      }

      this.attachBubbleListeners(container, bubbles, context);

      // Mark as initialized
      container.dataset.bubblesInitialized = 'true';
      container.classList.remove('product-bubbles--loading');

    } catch (error) {
      // console.error('[Bubbles] Error initializing with cache:', error);
      container.classList.remove('product-bubbles--loading');
    }
  }

  renderBubblesFromData(container, products, currentProductId, section) {
    // Find the wrapper element to render bubbles into (preserves label)
    let wrapper = container.querySelector('.product-bubbles__wrapper');
    if (!wrapper) {
      // Fallback: create wrapper if it doesn't exist
      wrapper = document.createElement('div');
      wrapper.className = 'product-bubbles__wrapper';
      container.appendChild(wrapper);
    }

    // Clear existing bubbles (only in wrapper, preserves label)
    wrapper.innerHTML = '';

    if (!products || products.length === 0) {
      // this.debug && console.log('[Bubbles] No products to render');
      return;
    }

    // Create bubble elements (products already have metafields and are sorted)
    products.forEach(product => {
      const bubble = this.createBubbleElement(product, currentProductId, section);
      if (bubble) {
        wrapper.appendChild(bubble);
      }
    });

    // Update the color label with current product's color name
    this.updateColorLabel(container, currentProductId, products);

    // this.debug && console.log(`[Bubbles] Rendered ${wrapper.children.length} bubbles to DOM`);
  }

  updateColorLabel(container, currentProductId, products) {
    const labelValue = container.querySelector('.product-bubbles__label-value');
    if (!labelValue) return;

    // Find current product to get its title
    const currentProduct = products.find(p => String(p.id) === String(currentProductId));
    if (currentProduct && currentProduct.title) {
      // Extract color name from title (format: "Product Name - Color Name")
      const titleParts = currentProduct.title.split(' - ');
      const colorName = titleParts.length > 1 ? titleParts[titleParts.length - 1] : currentProduct.title;
      labelValue.textContent = colorName;
    }
  }

  /**
   * Triggers Flickity resize when bubbles are inside a carousel
   * Waits for images to load before resizing for accurate dimensions
   * IMPORTANT: Calls reloadCells() before resize() per Flickity v2.3.0 best practices
   */
  triggerFlickityResize(container, retryCount = 0) {
    // Only trigger for collection context (where Flickity is used)
    const context = container.dataset.context;
    if (context !== 'collection') {
      return;
    }

    // Find parent Flickity slider
    const flickitySlider = container.closest('.flickity-slider');
    if (!flickitySlider) {
      // this.debug && console.log('[Bubbles] Not in a Flickity slider, skipping resize');
      return;
    }

    // CRITICAL: Flickity is initialized on slide-show element, not flickity-viewport
    // DOM structure: slide-show > flickity-viewport > flickity-slider
    // So we need to go up TWO levels from flickity-slider
    const flickityViewport = flickitySlider.parentElement;
    let flickityElement = flickityViewport.parentElement; // Should be slide-show

    // Try to get Flickity instance - try both parent and viewport
    let flkty = window.Flickity ? window.Flickity.data(flickityElement) : null;
    let isInitiated = flickityElement.dataset.initiated === 'true';

    // If not found on parent, try the viewport itself (fallback for different carousel types)
    if (!flkty) {
      flkty = window.Flickity ? window.Flickity.data(flickityViewport) : null;
      if (flkty) {
        flickityElement = flickityViewport;
        isInitiated = flickityElement.dataset.initiated === 'true';
      }
    }

    if (!flkty || !isInitiated) {
      // this.debug && console.log(`[Bubbles] Flickity not ready - retry attempt ${retryCount}/5`);

      // Retry up to 5 times with increasing delays (for slow-loading sections)
      if (retryCount < 5) {
        const delay = 100 * (retryCount + 1); // 100ms, 200ms, 300ms, 400ms, 500ms
        setTimeout(() => {
          this.triggerFlickityResize(container, retryCount + 1);
        }, delay);
      }
      return;
    }

    // this.debug && console.log('[Bubbles] Flickity is ready, proceeding with resize');

    // Check if bubbles contain images
    const images = container.querySelectorAll('img');

    const performResize = () => {
      // Debounce multiple resize calls
      clearTimeout(this.flickityResizeTimer);
      this.flickityResizeTimer = setTimeout(() => {
        requestAnimationFrame(() => {
          try {
            // CRITICAL: Call reloadCells() first per Flickity v2.3.0 best practices
            // This tells Flickity to remeasure all cells before resizing
            flkty.reloadCells();

            // Now resize to recalculate positions with new cell heights
            flkty.resize();

            // this.debug && console.log('[Bubbles] Flickity resize complete');

            // Recenter arrows for product carousels
            if (flickityElement.classList.contains('products')) {
              const prevButton = flickityElement.parentElement.querySelector('.flickity-prev');
              const nextButton = flickityElement.parentElement.querySelector('.flickity-next');
              if (prevButton && nextButton) {
                window.dispatchEvent(new Event('resize'));
              }
            }
          } catch (error) {
            // console.error('[Bubbles] Error during Flickity resize:', error);
          }
        });
      }, 50); // 50ms debounce
    };

    // If images exist, wait for them to load
    if (images.length > 0) {
      let loadedCount = 0;
      let hasExplicitDimensions = true;

      images.forEach(img => {
        // Check if image has explicit width/height
        if (!img.width || !img.height) {
          hasExplicitDimensions = false;
        }

        if (img.complete) {
          loadedCount++;
        } else {
          img.addEventListener('load', () => {
            loadedCount++;
            if (loadedCount === images.length) {
              performResize();
            }
          });
        }
      });

      // Timeout fallback in case images fail to load
      const timeoutId = setTimeout(() => {
        if (loadedCount < images.length) {
          // this.debug && console.log('[Bubbles] Some images not loaded after 2s, resizing anyway');
          performResize();
        }
      }, 2000);

      // If all images loaded or have explicit dimensions, resize immediately
      if (loadedCount === images.length || hasExplicitDimensions) {
        clearTimeout(timeoutId);
        performResize();
      }
    } else {
      // No images, resize immediately
      performResize();
    }
  }

  createBubbleElement(product, currentProductId, section) {
    // this.debug && console.log('[Bubbles] Creating bubble for product:', product);

    const bubble = document.createElement('a');
    bubble.className = 'product-bubble';

    // Get style bubble image - check metafield first, then fallback
    let bubbleImage = null;

    // Priority 1: Custom bubble image from metafield (if available)
    // Handle both custom API format and Liquid metafield format
    if (product.metafields?.bubble_image?.exists && product.metafields.bubble_image.src) {
      // Custom API format: { metafields: { bubble_image: { src: "...", exists: true } } }
      bubbleImage = product.metafields.bubble_image.src;
      // this.debug && console.log('[Bubbles] Using custom bubble_image metafield (custom API):', bubbleImage);
    } else if (product.metafields?.custom?.bubble_image) {
      // Liquid format: { metafields: { custom: { bubble_image: "..." } } }
      bubbleImage = product.metafields.custom.bubble_image.value || product.metafields.custom.bubble_image;
      // this.debug && console.log('[Bubbles] Using custom bubble_image metafield (Liquid):', bubbleImage);
    }

    // Priority 2: Featured image
    if (!bubbleImage) {
      bubbleImage = product.featured_image || product.image;
    }

    // If featured_image is an object, get the src property
    if (bubbleImage && typeof bubbleImage === 'object') {
      bubbleImage = bubbleImage.src;
    }

    // Priority 3: Fallback to first image in images array
    if (!bubbleImage && product.images && product.images.length > 0) {
      bubbleImage = typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src;
    }

    // Generate thumbnail URL (100x100) - Shopify image transformation
    // Skip if already a thumbnail URL (custom API returns pre-sized images)
    if (bubbleImage && !bubbleImage.includes('_100x') && !bubbleImage.includes('_160x')) {
      // Remove any existing size parameters and query strings
      bubbleImage = bubbleImage.split('?')[0];
      // Add Shopify image size parameter for 100x100 thumbnail
      bubbleImage = bubbleImage.replace(/\.(jpg|jpeg|png|webp|gif)/i, '_100x.$1');
    }

    // Check if this is the current product
    const isActive = product.id?.toString() === currentProductId?.toString();
    if (isActive) {
      bubble.classList.add('product-bubble--active');
      bubble.setAttribute('aria-current', 'true');
    }

    // Check if sold out
    // Debug logging to understand availability data
    // this.debug && console.log('[Bubbles] Availability check for', product.title, {
    //   available: product.available,
    //   type: typeof product.available,
    //   variants: product.variants?.length,
    //   firstVariantAvailable: product.variants?.[0]?.available
    // });

    // Product is sold out if available is explicitly false or "false" string
    // Handle both boolean and string values from API
    let isSoldOut = false;
    if (product.available === false || product.available === 'false' || product.available === null) {
      isSoldOut = true;
    } else if (product.available === undefined) {
      // Fallback: check if any variant is available
      if (product.variants && product.variants.length > 0) {
        isSoldOut = !product.variants.some(v => v.available === true || v.available === 'true');
      }
    }

    if (isSoldOut) {
      bubble.classList.add('product-bubble--sold-out');
      // this.debug && console.log('[Bubbles] Marking as sold out:', product.title);
    }

    // Ensure we have required properties
    if (!product.handle) {
      // console.error('[Bubbles] Product missing handle:', product);
      return null;
    }

    // Set data attributes
    bubble.dataset.productId = product.id;
    bubble.dataset.productHandle = product.handle;
    bubble.dataset.productTitle = product.title || 'Untitled';
    bubble.dataset.productPrice = product.price || 0;
    bubble.dataset.productImage = bubbleImage || '';
    // Preserve collection context in URL (e.g., /collections/bridesmaids/products/...)
    // so bubbles don't navigate users away from the current collection site
    const pathParts = window.location.pathname.split('/');
    const collectionsIndex = pathParts.indexOf('collections');
    if (collectionsIndex !== -1 && pathParts[collectionsIndex + 1]) {
      bubble.dataset.productUrl = `/collections/${pathParts[collectionsIndex + 1]}/products/${product.handle}`;
    } else {
      bubble.dataset.productUrl = `/products/${product.handle}`;
    }
    bubble.href = bubble.dataset.productUrl;

    if (product.variants && product.variants.length > 0) {
      bubble.dataset.variantId = product.variants[0].id;
    }

    // Create image element with proper CSS structure
    if (bubbleImage) {
      // Create wrapper span (required by CSS: .product-bubble__image)
      const imageWrapper = document.createElement('span');
      imageWrapper.className = 'product-bubble__image';

      // Create img element
      const img = document.createElement('img');
      img.src = bubbleImage;
      img.alt = product.title || 'Product image';
      img.loading = 'lazy';
      img.width = 40;
      img.height = 40;

      imageWrapper.appendChild(img);
      bubble.appendChild(imageWrapper);
    } else {
      // Fallback to text if no image
      const placeholder = document.createElement('span');
      placeholder.className = 'product-bubble__placeholder';
      placeholder.textContent = product.title ? product.title.charAt(0) : '?';
      bubble.appendChild(placeholder);
    }

    // Add sold-out line indicator
    if (isSoldOut) {
      const line = document.createElement('span');
      line.className = 'product-bubble__sold-out-line';
      bubble.appendChild(line);
    }

    return bubble;
  }

  attachBubbleListeners(container, bubbles, context) {
    bubbles.forEach(bubble => {
      // Remove any existing click handlers by cloning
      const newBubble = bubble.cloneNode(true);
      bubble.parentNode.replaceChild(newBubble, bubble);

      newBubble.addEventListener('click', (e) => {
        // console.log(`Bubble clicked in context: ${context}`);

        // Don't switch if already active or sold out
        if (newBubble.classList.contains('product-bubble--active') ||
            newBubble.classList.contains('product-bubble--sold-out')) {
          e.preventDefault();
          return;
        }

        // Handle based on context
        if (context === 'collection') {
          e.preventDefault();
          this.handleCollectionSwitch(newBubble, container);
        } else if (context === 'quickview') {
          e.preventDefault();
          this.handleQuickViewSwitch(newBubble, container);
        } else {
          // Use regular links for product pages
          const productUrl = newBubble.dataset.productUrl;
          if (productUrl) {
            window.location.href = productUrl;
          }
        }
      });

      // Add hover effect
      newBubble.addEventListener('mouseenter', () => {
        this.showTooltip(newBubble);
      });

      newBubble.addEventListener('mouseleave', () => {
        this.hideTooltip(newBubble);
      });
    });
  }

  setActiveBubble(bubble, container) {
    // Remove active class from all bubbles
    container.querySelectorAll('.product-bubble').forEach(b => {
      b.classList.remove('product-bubble--active');
      b.removeAttribute('aria-current');
    });

    // Add active class to clicked bubble
    bubble.classList.add('product-bubble--active');
    bubble.setAttribute('aria-current', 'true');
  }

  setLoading(container, isLoading) {
    if (isLoading) {
      container.classList.add('product-bubbles--loading');
    } else {
      container.classList.remove('product-bubbles--loading');
    }
  }

  showTooltip(bubble) {
    const title = bubble.dataset.productTitle;
    if (!title) return;
    
    // Create tooltip if it doesn't exist
    let tooltip = bubble.querySelector('.product-bubble__tooltip');
    if (!tooltip) {
      tooltip = document.createElement('span');
      tooltip.className = 'product-bubble__tooltip';
      tooltip.textContent = title;
      bubble.appendChild(tooltip);
    }
    
    tooltip.classList.add('product-bubble__tooltip--visible');
  }

  hideTooltip(bubble) {
    const tooltip = bubble.querySelector('.product-bubble__tooltip');
    if (tooltip) {
      tooltip.classList.remove('product-bubble__tooltip--visible');
    }
  }

  triggerProductChange(productData) {
    // Dispatch custom event for other scripts to listen to
    const event = new CustomEvent('bubble:productChanged', {
      detail: productData,
      bubbles: true
    });
    document.dispatchEvent(event);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productBubbles = new ProductBubbles();
  });
} else {
  window.productBubbles = new ProductBubbles();
}

// Reinitialize after dynamic content loads (AJAX)
document.addEventListener('shopify:section:load', () => {
  window.productBubbles = new ProductBubbles();
});

// Listen for pagination events to reinitialize bubbles on lazy loaded products
document.addEventListener('pagination:page-changed', (e) => {
  // console.log('Pagination changed, reinitializing bubbles for new products...');
  setTimeout(() => {
    const newBubbleContainers = document.querySelectorAll('.product-bubbles:not([data-bubbles-initialized])');
    newBubbleContainers.forEach(container => {
      // Set context to collection for lazy loaded products
      container.dataset.context = 'collection';
      if (window.productBubbles) {
        window.productBubbles.initContainer(container);
        // Trigger resize after initialization
        window.productBubbles.triggerFlickityResize(container);
      }
    });
  }, 100);
});

// Listen for quick view open events to initialize bubbles in the modal
document.addEventListener('quick-view:open', (e) => {
  // console.log('Quick view opened, initializing bubbles...');
  // Wait for content to be loaded
  setTimeout(() => {
    const quickViewBubbles = document.querySelectorAll('#Product-Drawer .product-bubbles');
    quickViewBubbles.forEach(container => {
      // Force context to quickview for bubbles in the modal
      container.dataset.context = 'quickview';
      // Reinitialize this specific container
      if (window.productBubbles) {
        window.productBubbles.initContainer(container);
      }
    });
  }, 100);
});

// Set up MutationObserver to watch for quick view content
function setupQuickViewObserver() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        // Check if product bubbles were added to the quick view
        const quickViewBubbles = document.querySelectorAll('#Product-Drawer .product-bubbles:not([data-bubbles-initialized])');
        if (quickViewBubbles.length > 0) {
          // console.log('Found new quick view bubbles, initializing...');
          quickViewBubbles.forEach(container => {
            container.dataset.context = 'quickview';
            container.dataset.reinitialize = 'true';
            if (window.productBubbles) {
              window.productBubbles.initContainer(container);
            }
          });
        }
      }
    });
  });

  // Start observing the quick view modal for changes
  const quickViewModal = document.getElementById('Product-Drawer');
  if (quickViewModal) {
    //  console.log('Setting up MutationObserver for quick view modal');
    observer.observe(quickViewModal, {
      childList: true,
      subtree: true
    });
  } else {
    // If modal doesn't exist yet, wait for it
    setTimeout(() => {
      const modal = document.getElementById('Product-Drawer');
      if (modal) {
        // console.log('Quick view modal found, setting up observer');
        observer.observe(modal, {
          childList: true,
          subtree: true
        });
      }
    }, 1000);
  }
}

// Set up the observer when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupQuickViewObserver);
} else {
  setupQuickViewObserver();
}

// Listen for collection tab clicks to trigger resize after tab switching
document.addEventListener('click', (e) => {
  const collectionTabLink = e.target.closest('collection-tabs a[data-collection]');
  if (collectionTabLink) {
    const collection = collectionTabLink.dataset.collection;

    // Wait for tab switch and bubble initialization to complete
    setTimeout(() => {
      // Find all bubble containers in the now-visible products
      const visibleBubbles = document.querySelectorAll(`[data-collection="${collection}"] .product-bubbles`);

      if (visibleBubbles.length > 0 && window.productBubbles) {
        // Trigger resize for each visible bubble container
        visibleBubbles.forEach(container => {
          window.productBubbles.triggerFlickityResize(container);
        });
      }
    }, 200); // Wait for tab switch animation and bubble loading
  }
});